import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';

export function constrainBoardPanelPosition(
  point: { readonly x: number; readonly y: number },
  panel: { readonly width: number; readonly height: number },
  viewport: { readonly width: number; readonly height: number },
) {
  return {
    x: Math.max(8, Math.min(point.x, viewport.width - panel.width - 8)),
    y: Math.max(8, Math.min(point.y, viewport.height - panel.height - 8)),
  };
}

/** Presentation only: tools start in their dock and can be moved without losing their state. */
export function useBoardPanelDrag({
  preserveWidth = false,
}: { readonly preserveWidth?: boolean } = {}) {
  const panelRef = useRef<HTMLElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number; width?: number } | null>(null);
  const drag = useRef<{ x: number; y: number; left: number; top: number; width: number } | null>(
    null,
  );
  const resetPosition = () => {
    drag.current = null;
    setPosition(null);
  };
  useEffect(() => {
    window.addEventListener('resize', resetPosition);
    return () => window.removeEventListener('resize', resetPosition);
  }, []);
  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    if ((event.target as Element).closest('button, input, select, textarea') !== null) return;
    const bounds = panelRef.current?.getBoundingClientRect();
    if (bounds === undefined) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = {
      x: event.clientX,
      y: event.clientY,
      left: bounds.left,
      top: bounds.top,
      width: bounds.width,
    };
  };
  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    const bounds = panelRef.current?.getBoundingClientRect();
    if (drag.current === null || bounds === undefined) return;
    setPosition({
      ...constrainBoardPanelPosition(
        {
          x: drag.current.left + event.clientX - drag.current.x,
          y: drag.current.top + event.clientY - drag.current.y,
        },
        bounds,
        { width: window.innerWidth, height: window.innerHeight },
      ),
      ...(preserveWidth ? { width: drag.current.width } : {}),
    });
  };
  const endDrag = () => {
    drag.current = null;
  };
  const style: CSSProperties | undefined =
    position === null
      ? undefined
      : {
          position: 'fixed',
          left: position.x,
          top: position.y,
          right: 'auto',
          width: position.width,
          zIndex: 130,
        };
  return {
    panelRef,
    style,
    floating: position !== null,
    resetPosition,
    dragHandlers: { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag },
  };
}
